import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate, useLocation, Routes, Route, Navigate } from 'react-router-dom';
import LanguageSwitcher from '../LanguageSwitcher';
import '../Dashboard/Dashboard.css';
import './AdminHome.css';

import Organizations from './Organization/Organizations';
import UserManagement from './User/UserManagement';
import CVE from './CVE/CVE';
import ScheduledJobsPage from './ScheduledJobs/ScheduledJobs';
import ASMDashboard from './AsmDashboard';
import PartnersPage from './Partners/PartnersPage';
import AdminBillingPage from './Billing/AdminBillingPage';
import PlatformDashboard from './PlatformDashboard';
import MetadataLibrary from './MetadataLibrary/MetadataLibrary';
import CWE from './CWE/CWE';
import AdminAudits from './Audits/AdminAudits';
import AdminMarketplacePage from './Marketplace/AdminMarketplacePage';
import MappingEditor from './Compliance/MappingEditor';
import RedTeamPlanning from './RedTeam/RedTeamPlanning';
import InternalAsmDashboard from './iasm/InternalAsmDashboard';
import UserActivityPage from './UserActivity/UserActivityPage';
import PasswordResetAudit from './UserActivity/PasswordResetAudit';
import JobApplicants from './JobApplicants/JobApplicants';
import ThreatIntel from './ThreatIntel/ThreatIntel';
import PublicSnapshots from './PublicSnapshots/PublicSnapshots';
import SeedKeys from './SeedKeys/SeedKeys';
import SupportConsole from './Support/SupportConsole';
import EngagementConsole from './Engagements/EngagementConsole';
import AgreementTemplates from './Engagements/AgreementTemplates';
import axiosInstance from '../api';
import { isPlatformAdmin } from '../../utils/platformRole';
import TwoFactorAuth from '../Settings/TwoFactorAuth';
import ChangePassword from '../Settings/ChangePassword';
import '../Settings/Settings.css';

function AdminHome() {
  const { t } = useTranslation();
  // Billing is platform-admin ONLY — the restricted platform_user role has no billing access
  // (carve-out #2). Hide the nav item and guard the route below; the API 403s it regardless.
  const canBilling = isPlatformAdmin();
  const ADMIN_NAV = [
    {
      label: t('AdminHome.navOverview'),
      items: [
        { to: '/adminHome/overview',        icon: 'fa-th-large',       label: t('AdminHome.navPlatformOverview') },
      ],
    },
    {
      label: t('AdminHome.navManagement'),
      items: [
        { to: '/adminHome/organisations',   icon: 'fa-building',       label: t('AdminHome.navOrganisations') },
        { to: '/adminHome/usersManagement', icon: 'fa-users-cog',      label: t('AdminHome.navManageUsers') },
        { to: '/adminHome/audits',          icon: 'fa-clipboard-check',label: t('AdminHome.navAudits') },
        { to: '/adminHome/user-activity',   icon: 'fa-user-clock',     label: t('AdminHome.navUserActivity') },
        { to: '/adminHome/password-resets', icon: 'fa-key',            label: t('AdminHome.navPasswordResets') },
        { to: '/adminHome/applicants',      icon: 'fa-file-upload',    label: t('AdminHome.navJobApplicants') },
        { to: '/adminHome/support',         icon: 'fa-life-ring',      label: t('AdminHome.navSupportTickets', 'Support Tickets') },
        { to: '/adminHome/engagements',     icon: 'fa-file-signature', label: t('AdminHome.navEngagements', 'Engagements') },
      ],
    },
    {
      label: t('AdminHome.navSecurity'),
      items: [
        { to: '/adminHome/asm',             icon: 'fa-globe',            label: t('AdminHome.navAttackSurface') },
        { to: '/adminHome/public-snapshots',icon: 'fa-bullseye',         label: 'Public Snapshots' },
        { to: '/adminHome/iasm',            icon: 'fa-network-wired',    label: t('AdminHome.navInternalAsm') },
        { to: '/adminHome/red-team',        icon: 'fa-skull-crossbones', label: t('AdminHome.navRedTeamPlanning') },
        { to: '/adminHome/threat-intel',    icon: 'fa-radiation',        label: t('AdminHome.navThreatIntel') },
        { to: '/adminHome/ingest-keys',     icon: 'fa-key',              label: t('AdminHome.navIngestKeys') },
        { to: '/adminHome/cve',             icon: 'fa-bug',            label: t('AdminHome.navCve') },
        { to: '/adminHome/cwe',             icon: 'fa-code',           label: t('AdminHome.navCwe') },
        { to: '/adminHome/ScheduledJobs',   icon: 'fa-calendar-alt',   label: t('AdminHome.navScheduledJobs') },
      ],
    },
    {
      label: t('AdminHome.navCompliance'),
      items: [
        { to: '/adminHome/compliance/library',   icon: 'fa-database',        label: t('AdminHome.navMetadataLibrary') },
        { to: '/adminHome/compliance/mappings',  icon: 'fa-project-diagram', label: t('AdminHome.navControlMappings') },
        { to: '/adminHome/agreement-templates',  icon: 'fa-file-contract',   label: t('AdminHome.navAgreementTemplates', 'Agreement Templates') },
      ],
    },
    {
      label: t('AdminHome.navMarketplace'),
      items: [
        { to: '/adminHome/partners',    icon: 'fa-network-wired', label: t('AdminHome.navPartners') },
        { to: '/adminHome/marketplace', icon: 'fa-store',         label: t('AdminHome.navListingQueue') },
        ...(canBilling
          ? [{ to: '/adminHome/billing', icon: 'fa-credit-card', label: t('AdminHome.navBilling') }]
          : []),
      ],
    },
    {
      label: t('AdminHome.navAccount'),
      items: [
        { to: '/adminHome/account-security', icon: 'fa-user-shield', label: t('AdminHome.navMyAccount2fa') },
      ],
    },
  ];
  const [collapsed, setCollapsed] = useState(false);
  const navigate  = useNavigate();
  const location  = useLocation();
  // Foldable sections: keyed by index (labels are translated). Remembered per browser; the
  // section holding the current page is always opened so the active item is never hidden.
  const NAV_FOLD_KEY = 'adminNavOpenSections';
  const sectionHasPath = (sec, path) => sec.items.some(i => path === i.to || path.startsWith(i.to + '/'));
  const [openSections, setOpenSections] = useState(() => {
    let saved = null;
    try { saved = JSON.parse(localStorage.getItem(NAV_FOLD_KEY)); } catch (_) { /* no-op */ }
    const open = new Set(Array.isArray(saved) ? saved : [0]);
    ADMIN_NAV.forEach((sec, idx) => { if (sectionHasPath(sec, location.pathname)) open.add(idx); });
    return open;
  });
  const persistOpen = (set) => {
    try { localStorage.setItem(NAV_FOLD_KEY, JSON.stringify([...set])); } catch (_) { /* no-op */ }
  };
  const toggleSection = (idx) => setOpenSections(prev => {
    const next = new Set(prev);
    next.has(idx) ? next.delete(idx) : next.add(idx);
    persistOpen(next);
    return next;
  });
  useEffect(() => {
    const idx = ADMIN_NAV.findIndex(sec => sectionHasPath(sec, location.pathname));
    if (idx >= 0) setOpenSections(prev => (prev.has(idx) ? prev : new Set(prev).add(idx)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  const username  = localStorage.getItem('userName') || localStorage.getItem('username') || 'Admin';
  // Mandatory 2FA gate for platform admins/users. null = checking, false = not enrolled (blocked).
  // Default = mandatory (prod). A build with REACT_APP_MFA_MANDATORY='false' (integ) makes 2FA opt-in.
  const MFA_MANDATORY = process.env.REACT_APP_MFA_MANDATORY !== 'false';
  const [mfaEnabled, setMfaEnabled] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem('userName');
    if (stored) localStorage.setItem('username', stored);
  }, []);

  useEffect(() => {
    axiosInstance.get('/mfa/status')
      .then(r => setMfaEnabled(!!r.data.enabled))
      .catch(() => setMfaEnabled(true)); // fail-open so a transient error never bricks admin access
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('username');
    localStorage.removeItem('orgRole');
    navigate('/login');
  };

  const getPageTitle = () => {
    const p = location.pathname;
    if (p.includes('/overview'))               return t('AdminHome.titlePlatformOverview');
    if (p.includes('/organisations'))          return t('AdminHome.titleOrganisations');
    if (p.includes('/usersManagement'))        return t('AdminHome.titleManageUsers');
    if (p.includes('/iasm'))                   return t('AdminHome.titleInternalAttackSurface');
    if (p.includes('/asm'))                    return t('AdminHome.titleAttackSurface');
    if (p.includes('/red-team'))               return t('AdminHome.titleRedTeamPlanning');
    if (p.includes('/threat-intel'))           return t('AdminHome.titleThreatIntel');
    if (p.includes('/ingest-keys'))            return t('AdminHome.titleIngestKeys');
    if (p.includes('/cve'))                    return t('AdminHome.titleCve');
    if (p.includes('/cwe'))                    return t('AdminHome.titleCwe');
    if (p.includes('/ScheduledJobs'))          return t('AdminHome.titleScheduledJobs');
    if (p.includes('/compliance/library'))     return t('AdminHome.titleMetadataLibrary');
    if (p.includes('/audits'))                 return t('AdminHome.titleAllAudits');
    if (p.includes('/password-resets'))        return t('AdminHome.titlePasswordResetAudit');
    if (p.includes('/user-activity'))          return t('AdminHome.titleUserActivityLog');
    if (p.includes('/applicants'))             return t('AdminHome.titleJobApplicants');
    if (p.includes('/support'))                return t('AdminHome.titleSupportTickets', 'Support Tickets');
    if (p.includes('/partners'))               return t('AdminHome.titlePartnerEcosystem');
    if (p.includes('/marketplace'))            return t('AdminHome.titleMarketplaceListingQueue');
    if (p.includes('/billing'))                return t('AdminHome.titleBillingSubscriptions');
    if (p.includes('/compliance/mappings'))    return t('AdminHome.titleControlMappingLibrary');
    if (p.includes('/account-security'))       return t('AdminHome.titleMyAccount2fa');
    return t('AdminHome.titleAdmin');
  };

  const initials = username.slice(0, 2).toUpperCase();

  // Block the console until a platform admin enrolls in 2FA (skipped when MFA is optional in this env).
  if (MFA_MANDATORY && mfaEnabled === false) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                    background: '#f5f5f5', padding: '48px 16px' }}>
        <div style={{ maxWidth: 560, width: '100%', background: '#fff', border: '1px solid #e5e5e5',
                      borderRadius: 12, padding: 28 }}>
          <h2 style={{ marginTop: 0 }}>{t('AdminHome.secureAccountHeading')}</h2>
          <p style={{ color: '#475569', fontSize: 14, marginBottom: 8 }}>
            {t('AdminHome.secureAccountBody')}
          </p>
          <TwoFactorAuth onStatusChange={(s) => setMfaEnabled(!!s.enabled)} />
          <button className="secondary-btn" onClick={handleLogout} style={{ marginTop: 16 }}>
            <i className="fas fa-sign-out-alt" /> {t('AdminHome.signOut')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`db-layout ${collapsed ? 'db-collapsed' : ''}`}>
      {/* ── Sidebar ─────────────────────────────────── */}
      <aside className="db-sidebar">
        {/* Brand */}
        <div className="db-brand">
          <div className="db-brand-logo db-brand-logo-admin">
            <i className="fas fa-shield-alt" />
          </div>
          <div className="db-brand-text">
            <span className="db-brand-name">CATAAM</span>
            <span className="db-brand-sub db-brand-sub-admin">{t('AdminHome.platformAdmin')}</span>
          </div>
          <button className="db-collapse-btn" onClick={() => setCollapsed(c => !c)} title={collapsed ? t('AdminHome.expand') : t('AdminHome.collapse')}>
            <i className={`fas fa-chevron-${collapsed ? 'right' : 'left'}`} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="db-nav">
          {ADMIN_NAV.map((section, idx) => {
            const isOpen = openSections.has(idx);
            return (
            <div key={section.label} className="db-section">
              <button
                className="db-section-label"
                onClick={() => !collapsed && toggleSection(idx)}
                title={collapsed ? section.label : undefined}
                aria-expanded={isOpen}
              >
                <span className="db-section-label-text">{section.label}</span>
                {!collapsed && (
                  <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} db-section-chevron`} />
                )}
              </button>
              {(isOpen || collapsed) && section.items.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => `db-item${isActive ? ' db-item-active' : ''}`}
                  title={collapsed ? item.label : undefined}
                >
                  <i className={`fas ${item.icon} db-item-icon`} />
                  <span className="db-item-label">{item.label}</span>
                </NavLink>
              ))}
            </div>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="db-sidebar-footer">
          {!collapsed && (
            <div style={{ marginBottom: 10 }}>
              <LanguageSwitcher />
            </div>
          )}
          <div className="db-user">
            <div className="db-user-avatar db-user-avatar-admin">{initials}</div>
            <div className="db-user-info">
              <div className="db-user-name">{username}</div>
              <div className="db-user-org">{t('AdminHome.platformAdmin')}</div>
            </div>
          </div>
          <button className="db-logout-btn" onClick={handleLogout} title={t('AdminHome.signOut')}>
            <i className="fas fa-sign-out-alt" />
          </button>
        </div>
      </aside>

      {/* ── Main area ───────────────────────────────── */}
      <div className="db-main">
        <header className="db-topbar">
          <h1 className="db-topbar-title">{getPageTitle()}</h1>
        </header>

        <main className="db-content">
          <Routes>
            <Route path="/"                                               element={<Navigate to="/adminHome/overview" />} />
            <Route path="/overview"                                       element={<PlatformDashboard />} />
            <Route path="/organisations"                                  element={<Organizations />} />
            <Route path="/asm"                                            element={<ASMDashboard isAdmin={true} />} />
            <Route path="/iasm"                                           element={<InternalAsmDashboard />} />
            <Route path="/red-team"                                       element={<RedTeamPlanning />} />
            <Route path="/threat-intel"                                   element={<ThreatIntel />} />
            <Route path="/ingest-keys"                                    element={<SeedKeys />} />
            <Route path="/cve"                                            element={<CVE />} />
            <Route path="/cwe"                                            element={<CWE />} />
            <Route path="/public-snapshots"                               element={<PublicSnapshots />} />
            <Route path="/compliance/library"                             element={<MetadataLibrary />} />
            <Route path="/usersManagement"                                element={<UserManagement />} />
            <Route path="/ScheduledJobs"                                  element={<ScheduledJobsPage />} />
            <Route path="/audits"                                         element={<AdminAudits />} />
            <Route path="/user-activity"                                  element={<UserActivityPage />} />
            <Route path="/password-resets"                                element={<PasswordResetAudit />} />
            <Route path="/applicants"                                     element={<JobApplicants />} />
            <Route path="/support"                                        element={<SupportConsole />} />
            <Route path="/engagements"                                    element={<EngagementConsole />} />
            <Route path="/agreement-templates"                            element={<AgreementTemplates />} />
            <Route path="/partners"                                       element={<PartnersPage />} />
            <Route path="/marketplace"                                    element={<AdminMarketplacePage />} />
            <Route path="/compliance/mappings"                            element={<MappingEditor />} />
            <Route path="/billing"                                        element={canBilling ? <AdminBillingPage /> : <Navigate to="/adminHome/overview" replace />} />
            <Route path="/account-security"                               element={
              <div className="settings-section">
                <div className="settings-header"><h2>{t('AdminHome.myAccountSecurity')}</h2></div>
                <div className="settings-content-area">
                  <TwoFactorAuth />
                  <ChangePassword />
                </div>
              </div>
            } />
            <Route path="*"                                               element={<Navigate to="/adminHome/overview" />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

export default AdminHome;
